/**
 * TikTok Error Parser Module (Story 11.1, Task 5)
 *
 * Maps TikTok API error responses to standardized error types.
 *
 * TikTok error response shape:
 * { error: { code: string, message: string, log_id: string } }
 *
 * Success is indicated by error.code === "ok" (NOT HTTP status alone).
 */

// ============================================================================
// Types
// ============================================================================

export type TikTokErrorType =
  | "token_expired"
  | "permission_denied"
  | "rate_limited"
  | "provider_unavailable"
  | "unknown";

export interface TikTokApiError {
  type: TikTokErrorType;
  message: string;
  logId: string | null;
  retryable: boolean;
}

interface TikTokErrorResponse {
  error?: {
    code?: string;
    message?: string;
    log_id?: string;
  };
}

// ============================================================================
// Error Code Mapping
// ============================================================================

const ERROR_CODE_MAP: Record<string, { type: TikTokErrorType; retryable: boolean }> = {
  access_token_invalid: { type: "token_expired", retryable: false },
  token_has_expired: { type: "token_expired", retryable: false },
  access_denied: { type: "permission_denied", retryable: false },
  scope_not_authorized: { type: "permission_denied", retryable: false },
  rate_limit_exceeded: { type: "rate_limited", retryable: true },
  internal_error: { type: "provider_unavailable", retryable: true },
};

// ============================================================================
// Error Parser (Task 5.1)
// ============================================================================

/**
 * Parses a TikTok API error response into a standardized error type.
 *
 * @param responseBody - The parsed JSON body from a TikTok API response
 * @returns A standardized TikTok API error, or null if no error detected
 */
export function parseTikTokApiError(
  responseBody: unknown
): TikTokApiError | null {
  const body = responseBody as TikTokErrorResponse;

  if (!body?.error?.code) {
    return null;
  }

  // "ok" means success
  if (body.error.code === "ok") {
    return null;
  }

  const mapping = ERROR_CODE_MAP[body.error.code];
  const logId = body.error.log_id || null;
  const message = body.error.message || `TikTok API error: ${body.error.code}`;

  if (mapping) {
    return {
      type: mapping.type,
      message,
      logId,
      retryable: mapping.retryable,
    };
  }

  // Unknown error code
  return {
    type: "unknown",
    message,
    logId,
    retryable: false,
  };
}
